import React, { useCallback } from "react";
import Select from "react-select";
import { ReactComponent as UploadIcon } from "../../assets/icons/Cloud_Upload.svg";
import styles from "./index.module.css";
import {
  branchSelectStyles,
  categorySelectStyles,
  certificationKey,
  certificationSelectStyles,
  optionId,
  optionLabel,
  optionName,
  sectorSelectStyles,
  subsectorSelectStyles,
} from "./requirementCreationShared.js";

const fieldClassName = (stylesModule, validationState) =>
  `${stylesModule.input} ${
    validationState === "error" ? stylesModule.inputError : ""
  }`;

const RequirementTextField = ({
  label,
  placeholder,
  value,
  maxLength,
  validationState,
  errorMessage,
  onChange,
  multiline = false,
}) => {
  const errorClass = validationState === "error" ? styles.inputError : "";
  const controlClassName = multiline
    ? `${styles.textarea} ${errorClass}`
    : fieldClassName(styles, validationState);
  const controlStyle = multiline
    ? {
        borderColor: validationState === "error" ? "#dc2626" : "#909092",
      }
    : undefined;
  return (
    <div className={styles.fullWidthContainer}>
      <label className={styles.label}>{label}</label>
      {multiline ? (
        <textarea
          placeholder={placeholder}
          className={controlClassName}
          style={controlStyle}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
        />
      ) : (
        <input
          type="text"
          placeholder={placeholder}
          className={controlClassName}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
        />
      )}
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
    </div>
  );
};

export const RequirementGeneralFields = ({
  t,
  requirement,
  categories,
  subcategories,
  selectCategoryRef,
  selectSubcategoryRef,
  onCategoryChange,
  onSubcategoryChange,
  onTitleChange,
  onDueDateChange,
  onDescriptionChange,
  onLocationChange,
  onExperienceChange,
  getErrorMessage,
  getFieldValidationState,
}) => (
  <>
    <label className={styles.modalSubtitle}>
      {t("components.requirementCreationModal.generalInformation")}
    </label>
    <div className={styles.generalInfoSection}>
      <div className={styles.generalInfoRow}>
        <div className={styles.categoryContainer}>
          <label className={styles.label}>
            {t("components.requirementCreationModal.category")}
          </label>
          <Select
            ref={selectCategoryRef}
            options={categories}
            isSearchable={false}
            value={categories.find(
              (option) => option._id === requirement.idCategoria,
            )}
            onChange={onCategoryChange}
            placeholder={t(
              "components.requirementCreationModal.categoryPlaceholder",
            )}
            styles={categorySelectStyles}
            getOptionValue={optionId}
            formatOptionLabel={optionLabel}
          />
        </div>
        <div className={styles.categoryContainer}>
          <label className={styles.label}>
            {t("components.requirementCreationModal.subcategory")}
          </label>
          <Select
            ref={selectSubcategoryRef}
            options={subcategories}
            isDisabled={!requirement.idCategoria}
            isSearchable={false}
            value={subcategories.find(
              (option) => option.value === requirement.nombreSubcategoria,
            )}
            onChange={onSubcategoryChange}
            placeholder={t(
              "components.requirementCreationModal.subcategoryPlaceholder",
            )}
            styles={categorySelectStyles}
            getOptionValue={optionName}
            formatOptionLabel={optionLabel}
          />
        </div>
      </div>
      <div className={styles.generalInfoRow}>
        <div className={styles.titleContainer}>
          <label className={styles.label}>
            {t("components.requirementCreationModal.title")}
          </label>
          <input
            type="text"
            placeholder={t(
              "components.requirementCreationModal.titlePlaceholder",
            )}
            className={fieldClassName(
              styles,
              getFieldValidationState(
                "requerimientoTitulo",
                requirement.titulo || "",
              ),
            )}
            value={requirement.titulo || ""}
            onChange={onTitleChange}
            maxLength={100}
          />
          {getErrorMessage("requerimientoTitulo") && (
            <p className={styles.errorMessage}>
              {getErrorMessage("requerimientoTitulo")}
            </p>
          )}
        </div>
        <div className={styles.dueDateContainer}>
          <label className={styles.label}>
            {t("components.requirementCreationModal.dueDate")}
          </label>
          <input
            type="date"
            placeholder={t(
              "components.requirementCreationModal.dueDatePlaceholder",
            )}
            className={styles.input}
            style={{ borderColor: "#909092" }}
            value={requirement.fechaLimite || ""}
            onChange={onDueDateChange}
          />
        </div>
      </div>
      <div className={styles.generalInfoRow}>
        <RequirementTextField
          label={t("components.requirementCreationModal.description")}
          placeholder={t(
            "components.requirementCreationModal.descriptionPlaceholder",
          )}
          value={requirement.descripcion || ""}
          maxLength={300}
          multiline
          validationState={getFieldValidationState(
            "requerimientoDescripcion",
            requirement.descripcion || "",
          )}
          errorMessage={getErrorMessage("requerimientoDescripcion")}
          onChange={onDescriptionChange}
        />
      </div>
      <div className={styles.generalInfoRow}>
        <RequirementTextField
          label={t("components.requirementCreationModal.location")}
          placeholder={t(
            "components.requirementCreationModal.locationPlaceholder",
          )}
          value={requirement.ubicacion || ""}
          maxLength={100}
          validationState={getFieldValidationState(
            "requerimientoUbicacion",
            requirement.ubicacion || "",
          )}
          errorMessage={getErrorMessage("requerimientoUbicacion")}
          onChange={onLocationChange}
        />
      </div>
      <div className={styles.generalInfoRow}>
        <div className={styles.fullWidthContainer}>
          <label className={styles.label}>
            {t("components.requirementCreationModal.minimumExperience")}
          </label>
          <div className={styles.experienceInputContainer}>
            <input
              type="number"
              placeholder={t(
                "components.requirementCreationModal.minimumExperiencePlaceholder",
              )}
              className={`${styles.experienceInput} ${
                getFieldValidationState(
                  "requerimientoExperienciaMinima",
                  requirement.experienciaMinima || "",
                ) === "error"
                  ? styles.inputError
                  : ""
              }`}
              value={requirement.experienciaMinima || ""}
              onChange={onExperienceChange}
            />
            <span className={styles.experienceLabel}>
              {t(
                "components.requirementCreationModal.minimumExperienceInputLabel",
              )}
            </span>
          </div>
          {getErrorMessage("requerimientoExperienciaMinima") && (
            <p className={styles.errorMessage}>
              {getErrorMessage("requerimientoExperienciaMinima")}
            </p>
          )}
        </div>
      </div>
    </div>
  </>
);

const ScianSelect = ({
  label,
  selectRef,
  options,
  onChange,
  placeholder,
  stylesConfig,
  disabled = false,
  isSearchable = true,
}) => (
  <div className={styles.profileColumn}>
    <label className={styles.label}>{label}</label>
    <Select
      ref={selectRef}
      options={options}
      onChange={onChange}
      isSearchable={isSearchable}
      isDisabled={disabled}
      placeholder={placeholder}
      styles={stylesConfig}
      getOptionValue={optionName}
      formatOptionLabel={optionLabel}
    />
  </div>
);

export const RequirementScianFields = ({
  t,
  sectors,
  subSectors,
  branches,
  requirement,
  selectSectorRef,
  selectSubsectorRef,
  selectBranchRef,
  onSectorChange,
  onSubsectorChange,
  onBranchChange,
}) => (
  <>
    <label className={styles.modalSubtitle}>
      {t("components.requirementCreationModal.scianProfile")}
    </label>
    <div className={styles.profileSection}>
      <ScianSelect
        label={t("components.requirementCreationModal.sector")}
        selectRef={selectSectorRef}
        options={sectors}
        onChange={onSectorChange}
        placeholder={t("components.requirementCreationModal.sectorPlaceholder")}
        stylesConfig={sectorSelectStyles}
      />
      <ScianSelect
        label={t("components.requirementCreationModal.subsector")}
        selectRef={selectSubsectorRef}
        options={subSectors}
        onChange={onSubsectorChange}
        disabled={!requirement.sector}
        isSearchable={false}
        placeholder={t(
          "components.requirementCreationModal.subsectorPlaceholder",
        )}
        stylesConfig={subsectorSelectStyles}
      />
      <ScianSelect
        label={t("components.requirementCreationModal.branch")}
        selectRef={selectBranchRef}
        options={branches}
        onChange={onBranchChange}
        disabled={!requirement.subSector}
        isSearchable={false}
        placeholder={t("components.requirementCreationModal.branchPlaceholder")}
        stylesConfig={branchSelectStyles}
      />
    </div>
  </>
);

const CertificationRow = ({
  index,
  options,
  placeholder,
  onSelect,
  onDelete,
}) => {
  const handleSelect = useCallback(
    (selectedOption) => {
      onSelect(index, selectedOption);
    },
    [index, onSelect],
  );
  const handleDelete = useCallback(() => {
    onDelete(index);
  }, [index, onDelete]);
  return (
    <div className={styles.certificationItem}>
      <div className={styles.certificationInputs}>
        <Select
          options={options}
          isSearchable={false}
          onChange={handleSelect}
          placeholder={placeholder}
          styles={certificationSelectStyles}
          getOptionValue={optionId}
          formatOptionLabel={optionLabel}
        />
      </div>
      <button
        className={styles.deleteCertificationButton}
        onClick={handleDelete}
      >
        &times;
      </button>
    </div>
  );
};

export const RequirementOptionalFields = ({
  t,
  requirement,
  originalCertifications,
  onAddCertification,
  onSelectCertification,
  onDeleteCertification,
  imageRef,
  onDragEnter,
  onDragLeave,
  onDrop,
  tempImage,
  imageError,
  onImageChange,
  onVolumeChange,
  getErrorMessage,
  getFieldValidationState,
}) => (
  <>
    <div className={styles.subtitleContainer}>
      <div className={styles.subtitleRow}>
        <label className={styles.modalSubtitle}>
          {t("components.requirementCreationModal.optionalRequirements")}
        </label>
        <button
          type="button"
          className={styles.createCertificationButton}
          onClick={onAddCertification}
        >
          + {t("components.requirementCreationModal.addCertification")}
        </button>
      </div>
      <span className={styles.optionalRequirementsText}>
        {t("components.requirementCreationModal.warningMessage")}
      </span>
    </div>
    <div className={styles.optionalRequirementsSection}>
      <div className={styles.certificationsContainer}>
        <div className={styles.certificationList}>
          {requirement.certificaciones.length > 0 && (
            <div className={styles.certificationHeader}>
              <label className={styles.label}>
                {t("components.requirementCreationModal.certifications")}
              </label>
            </div>
          )}
          {requirement.certificaciones.length > 0 &&
            requirement.certificaciones.map((certificacion, index) => (
              <CertificationRow
                key={certificationKey(certificacion)}
                index={index}
                options={originalCertifications}
                placeholder={t(
                  "components.requirementCreationModal.certificationsPlaceholder",
                )}
                onSelect={onSelectCertification}
                onDelete={onDeleteCertification}
              />
            ))}
        </div>
      </div>
      <div className={styles.optionalRequirementsRow}>
        <div className={styles.imageContainer}>
          <label className={styles.label}>
            {t("components.requirementCreationModal.image")}
          </label>
          <div
            ref={imageRef}
            className={styles.dropFileInput}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            {tempImage ? (
              <img
                src={tempImage}
                alt={t("common.requirementImageAlt")}
                className={styles.uploadedImage}
              />
            ) : (
              <div className={styles.uploadContainer}>
                <UploadIcon className={styles.uploadIcon} />
                {imageError && (
                  <span className={styles.errorMessage}>{imageError}</span>
                )}
                <span className={styles.uploadTitle}>
                  {t("common.drag&drop")}
                </span>
                <span className={styles.uploadSubtitle}>
                  {t("common.yourImage")}{" "}
                  <span
                    style={{ color: "#1D5AE8" }}
                    className={styles.uploadSubtitle}
                  >
                    {t("common.browseFile")}
                  </span>
                </span>
              </div>
            )}
            <input
              className={styles.imageInput}
              type="file"
              accept="image/*"
              onChange={onImageChange}
            />
          </div>
        </div>
        <div className={styles.volumeContainer}>
          <label className={styles.label}>
            {t("components.requirementCreationModal.volume")}
          </label>
          <input
            type="text"
            placeholder={t(
              "components.requirementCreationModal.volumePlaceholder",
            )}
            className={fieldClassName(
              styles,
              getFieldValidationState(
                "requerimientoVolumen",
                requirement.volumen || "",
              ),
            )}
            value={requirement.volumen || ""}
            onChange={onVolumeChange}
            maxLength={100}
          />
          {getErrorMessage("requerimientoVolumen") && (
            <p className={styles.errorMessage}>
              {getErrorMessage("requerimientoVolumen")}
            </p>
          )}
        </div>
      </div>
    </div>
  </>
);
